import * as styles from "./TimesheetExportPopover.css";

import { clsx } from "clsx";
import { useMemo, useState } from "react";

import { Button } from "@frontend/components/atoms/Button";
import {
  Field,
  FieldContent,
  FieldGroup,
  FieldLabel,
} from "@frontend/components/atoms/Field";
import { Icon } from "@frontend/components/atoms/Icon";
import {
  MonthPicker,
  MonthPickerContent,
  MonthPickerTrigger,
  MonthPickerValue,
  formatMonth,
  monthBounds,
  parseMonth,
  thisMonth,
} from "@frontend/components/atoms/MonthPicker";
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverTitle,
} from "@frontend/components/atoms/Popover";
import { Toggle } from "@frontend/components/atoms/Toggle";
import { ToggleGroup } from "@frontend/components/atoms/ToggleGroup";
import { useIntlLocale } from "@frontend/hooks/useIntlLocale";
import { Trans, useLingui } from "@lingui/react/macro";

import { usePlannedEventsCount } from "../hooks/usePlannedEventsCount";
import {
  TIMESHEET_FORMATS,
  type TimesheetFormat,
  timesheetApi,
} from "../services";

export type TimesheetExportPopoverProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: number;
  employeeId: number | null;
  employeeName?: string;
  anchor?: HTMLElement | null;
};

export function TimesheetExportPopover({
  open,
  onOpenChange,
  session,
  employeeId,
  employeeName,
  anchor,
}: TimesheetExportPopoverProps) {
  const { t } = useLingui();
  const locale = useIntlLocale();

  const [picked, setPicked] = useState<{ session: number; month: string }>();
  const [format, setFormat] = useState<TimesheetFormat>("xls");

  const month =
    picked?.session === session ? picked.month : formatMonth(thisMonth());

  const period = useMemo(
    () => monthBounds(parseMonth(month) ?? thisMonth()),
    [month],
  );

  const planning = usePlannedEventsCount(employeeId, period, open);

  const empty = planning.count === 0;
  const canDownload = null !== employeeId && !planning.loading && !empty;

  const downloadUrl =
    null === employeeId
      ? undefined
      : timesheetApi.downloadUrl(employeeId, period, format);

  const formatLabels: Record<TimesheetFormat, string> = {
    xls: t`Excel`,
    pdf: t`PDF`,
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverContent
        anchor={anchor}
        side="bottom"
        align="end"
        className={styles.popup}
      >
        <PopoverTitle>
          <Trans>Timesheet</Trans>
        </PopoverTitle>

        {employeeName && (
          <PopoverDescription>{employeeName}</PopoverDescription>
        )}

        <FieldGroup orientation="vertical" size="sm">
          <Field>
            <FieldLabel>
              <Trans>Month</Trans>
            </FieldLabel>
            <FieldContent>
              <MonthPicker
                locale={locale}
                value={month}
                onValueChange={(next) =>
                  next && setPicked({ session, month: next })
                }
              >
                <MonthPickerTrigger>
                  <MonthPickerValue />
                </MonthPickerTrigger>
                <MonthPickerContent />
              </MonthPicker>
            </FieldContent>
          </Field>

          <Field>
            <FieldLabel>
              <Trans>Format</Trans>
            </FieldLabel>
            <FieldContent>
              <ToggleGroup
                variant="outline"
                value={[format]}
                onValueChange={(value) => {
                  if (value.length > 0) {
                    setFormat(value[0] as TimesheetFormat);
                  }
                }}
                fill
              >
                {TIMESHEET_FORMATS.map((value) => (
                  <Toggle key={value} value={value} size="sm">
                    {formatLabels[value]}
                  </Toggle>
                ))}
              </ToggleGroup>
            </FieldContent>
          </Field>
        </FieldGroup>

        <p
          className={clsx(styles.status, empty && styles.statusEmpty)}
          aria-live="polite"
        >
          {planning.loading && <Trans>Checking the planning…</Trans>}
          {empty && (
            <>
              <Icon name="TriangleAlert" size="sm" />
              <Trans>No hours planned this month.</Trans>
            </>
          )}
          {planning.error && <Trans>The planning could not be checked.</Trans>}
        </p>

        <div className={styles.footer}>
          <Button
            variant="primary"
            size="sm"
            disabled={!canDownload}
            leftSection={<Icon name="Download" size="sm" />}
            render={<a href={downloadUrl} />}
            onClick={() => onOpenChange(false)}
          >
            {t`Download`}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
